import { compact } from "lodash-es";
import { createReducer, getType } from "typesafe-actions";

import { Trip } from "@mapmycustomers/shared/types/entity";
import { RawFile } from "@mapmycustomers/shared/types/File";
import ViewState from "@mapmycustomers/shared/types/viewModel/ViewState";

import { fileCreatedAtDescComparator } from "@app/component/preview/util/comparators";
import FileListItem from "@app/types/FileListItem";
import tripFieldModel from "@app/util/fieldModel/tripFieldModel";

import TripStats from "../../components/MileageTracking/types/TripsStats";

import {
  applyTripListViewSettings,
  clearAllUploadedTripFiles,
  deleteTrip,
  deleteTripFile,
  deleteTrips,
  fetchTripList,
  fetchTripPreviewData,
  initializeTripListView,
  MileageTrackingActions,
  updateTrip,
  uploadTripFiles,
} from "./actions";
import TripRecordData from "./TripRecordData";

const INITIAL_RECORD_DATA: Readonly<TripRecordData> = {
  files: [],
  trip: undefined,
};

export interface MileageTrackingState {
  deleteLoading: boolean;
  deletingTrips: boolean;
  error: undefined | unknown;
  filesAdded: FileListItem[];
  filesToDeletedIds: RawFile["id"][];
  filesUploading: boolean;
  firstDateWithData: Date | undefined;
  initialized: boolean;
  loading: boolean;
  previewData: TripRecordData;
  previewLoading: boolean;
  stats: TripStats;
  totalFilteredRecords: number;
  totalRecords: number;
  trips: Trip[];
  updatingTrip?: boolean;
  viewState: ViewState;
}

export const mileageTrackingInitialState: MileageTrackingState = {
  deleteLoading: false,
  deletingTrips: false,
  error: undefined,
  filesAdded: [],
  filesToDeletedIds: [],
  filesUploading: false,
  firstDateWithData: undefined,
  initialized: false,
  // loading is true by default to avoid flashing a "no rows" overlay when the page
  // is just opened but loading is not yet started
  loading: true,
  previewData: INITIAL_RECORD_DATA,
  previewLoading: false,
  stats: {
    parkingFee: 0,
    tollFee: 0,
    totalCount: 0,
    totalDistance: 0,
    totalDuration: 0,
    value: 0,
  },
  totalFilteredRecords: 0,
  totalRecords: 0,
  trips: [],
  viewState: {
    ...tripFieldModel.getDefaultListViewState(),
  },
};

const mileageTracking = createReducer<MileageTrackingState, MileageTrackingActions>(
  mileageTrackingInitialState
)
  .handleType(getType(initializeTripListView.request), (state) => ({
    ...state,
    initialized: false,
  }))
  .handleType(getType(initializeTripListView.success), (state, { payload }) => ({
    ...state,
    firstDateWithData: payload.firstDateWithData,
    initialized: true,
    viewState: payload.viewState,
  }))
  .handleType(getType(applyTripListViewSettings), (state, { payload }) => ({
    ...state,
    viewState: {
      ...state.viewState,
      columns: payload.columns ?? state.viewState.columns,
      filter: payload.filter ?? state.viewState.filter,
      range: payload.range ?? state.viewState.range,
      // only update selectedSavedFilterId when it is explicitly present in a payload (even when it is `undefined`)
      selectedSavedFilterId:
        "selectedSavedFilterId" in payload
          ? payload.selectedSavedFilterId
          : state.viewState.selectedSavedFilterId,
      sort: payload.sort ?? state.viewState.sort,
    },
  }))
  .handleType(getType(fetchTripList.request), (state) => ({
    ...state,
    error: undefined,
    loading: true,
  }))
  .handleType(getType(fetchTripList.success), (state, { payload }) => ({
    ...state,
    loading: false,
    stats: payload.stats,
    totalFilteredRecords: payload.totalFilteredRecords,
    totalRecords: payload.totalRecords,
    trips: payload.trips,
  }))
  .handleType(getType(fetchTripList.failure), (state, { payload }) => ({
    ...state,
    error: payload,
    loading: false,
    trips: [],
  }))
  .handleType(getType(deleteTrips.request), (state) => ({ ...state, deletingTrips: true }))
  .handleType(getType(deleteTrips.success), (state) => ({ ...state, deletingTrips: false }))
  .handleType(getType(deleteTrips.failure), (state) => ({ ...state, deletingTrips: false }))
  .handleType(getType(fetchTripPreviewData.request), (state) => ({
    ...state,
    previewData: INITIAL_RECORD_DATA,
    // hasChanges: false,
    previewLoading: true,
  }))
  .handleType(getType(fetchTripPreviewData.success), (state, { payload }) => ({
    ...state,
    previewData: payload,
    // hasChanges: false,
    previewLoading: false,
  }))
  .handleType(getType(fetchTripPreviewData.failure), (state) => ({
    ...state,
    previewLoading: false,
  }))
  .handleType(getType(uploadTripFiles.request), (state) => ({
    ...state,
    filesUploading: true,
  }))
  .handleType(getType(uploadTripFiles.success), (state, action) => {
    const filesUploaded = compact(action.payload.map(({ uploadedFile }) => uploadedFile));
    return {
      ...state,
      filesAdded: [...state.filesAdded, ...action.payload],
      filesUploading: false,
      previewData: {
        ...state.previewData,
        files: [...filesUploaded, ...state.previewData.files].sort(fileCreatedAtDescComparator),
      },
    };
  })
  .handleType(getType(uploadTripFiles.failure), (state) => ({
    ...state,
    filesUploading: false,
  }))
  .handleType(getType(deleteTripFile.request), (state, { payload }) => ({
    ...state,
    filesAdded: state.filesAdded.filter((file) => file.uploadedFile?.id !== payload.id),
    filesToDeletedIds: [...state.filesToDeletedIds, payload.id],
    previewData: {
      ...state.previewData,
      files: state.previewData.files.filter((file) => file.id !== payload.id),
    },
  }))
  .handleType(getType(deleteTripFile.success), (state, { payload }) => ({
    ...state,
    filesToDeletedIds: state.filesToDeletedIds.filter((id) => id !== payload.file.id),
    previewData: payload.removed
      ? state.previewData
      : {
          ...state.previewData,
          files: [...state.previewData.files, payload.file].sort(fileCreatedAtDescComparator),
        },
  }))
  .handleType(getType(deleteTripFile.failure), (state, { payload }) => ({
    ...state,
    filesToDeletedIds: state.filesToDeletedIds.filter((id) => id !== payload.id),
    previewData: {
      ...state.previewData,
      files: [...state.previewData.files, payload].sort(fileCreatedAtDescComparator),
    },
  }))
  .handleType(getType(clearAllUploadedTripFiles), (state) => ({
    ...state,
    filesAdded: [],
    filesUploading: false,
  }))
  .handleType(getType(deleteTrip.request), (state) => ({
    ...state,
    deleteLoading: true,
  }))
  .handleType([getType(deleteTrip.success), getType(deleteTrip.failure)], (state) => ({
    ...state,
    deleteLoading: false,
  }))
  .handleType(getType(updateTrip.request), (state) => ({ ...state, updatingTrip: true }))
  .handleType(getType(updateTrip.success), (state, { payload }) => ({
    ...state,
    previewData: {
      ...state.previewData,
      trip: payload,
    },
    trips: state.trips.map((trip) => (trip.id === payload.id ? payload : trip)),
    updatingTrip: false,
  }))
  .handleType(getType(updateTrip.failure), (state) => ({ ...state, updatingTrip: false }));

export default mileageTracking;
